import Controller from '@ember/controller';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import {
  pledgeFrequencyLabels,
  pledgeOneTimeFrequency,
  calculateEndDateForOneTimePledge,
} from 'donor-elf-web/lib/pledge-utils';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import Settings from 'donor-elf-web/services/settings';
import { tracked } from '@glimmer/tracking';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import type { PledgeEditRouteModel } from 'donor-elf-web/routes/funds/fund/pledges/edit';
import AbilitiesService from 'donor-elf-web/services/abilities';

export default class PledgeEditController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare fundGoalService: FundGoalService;
  @service declare settings: Settings;

  model!: PledgeEditRouteModel;
  queryParams = ['contactId'];
  @tracked contactId?: string;
  @tracked error: any;

  @action
  templateWillDestroy() {
    this.contactId = undefined;
    this.error = undefined;
  }

  get cannotEditAllFields() {
    return (
      !this.model.record.isNew &&
      !this.model.record.isManual &&
      this.abilities.noAbility('edit_pledges')
    );
  }

  get canEditEndDateField() {
    return (
      this.abilities.hasAbility('edit_pledges') ||
      this.abilities.hasAbility('edit_system_pledge_end_date')
    );
  }

  get frequencyOptions(): SelectOption[] {
    let options = [
      {
        val: undefined,
        label: '',
      },
    ];
    Object.keys(pledgeFrequencyLabels).forEach((key) => {
      if (pledgeFrequencyLabels[key] !== 'Weekly') {
        options.push({
          val: Number(key),
          label: pledgeFrequencyLabels[key],
        });
      }
    });
    return options;
  }

  get isOneTimeFrequency() {
    return this.model.changeset.get('frequency') === pledgeOneTimeFrequency;
  }

  get oneTimeMonthlyAmount() {
    return (
      (this.model.changeset.get('amount') || 0) /
      Number(this.model.changeset.get('endDateMonths'))
    );
  }

  get hasYtdGoalType() {
    return this.fundGoalService.hasYtdGoalType;
  }

  @(task(function* () {
    let self: PledgeEditController = this;
    let changeset = self.model.changeset;
    yield changeset.validate();
    if (changeset.get('isValid')) {
      if (changeset.get('frequency') === pledgeOneTimeFrequency) {
        changeset.set(
          'endDate',
          calculateEndDateForOneTimePledge({
            startDate: changeset.get('startDate'),
            endDateMonths: changeset.get('endDateMonths'),
            hasYtdGoalType: self.hasYtdGoalType,
          }),
        );
      }
      try {
        yield changeset.save();
        if (isEmpty(self.model.fromURL)) {
          self.transitionToRoute(
            'funds.fund.contacts.contact',
            changeset.get('contactId'),
          );
        } else {
          self.transitionToRoute(self.model.fromURL);
        }
      } catch (error) {
        if (self.model.record.errors.length > 0) {
          self.model.record.errors.forEach(({ attribute, message }) => {
            changeset.pushErrors(attribute, message);
          });
        } else {
          self.error = error;
        }
      }
    }
  }).drop())
  save: any;

  @action
  handleFrequencyChanged(/* newFrequency */) {
    this.model.changeset.set('endDateMonths', '12');
  }
}
